import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery } from '@tanstack/react-query'
import { bookingService } from '@/api/services/bookingService'
import { usePendingBookingsCount } from '@/api/hooks'
import { useBookingsSeenStore } from '@/store/useBookingsSeenStore'
import { ChangeTrainerModal } from '@/features/bookings/ChangeTrainerModal'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { useDebounce, useSessionState, useScrollRestoration } from '@/shared/hooks'
import { DateRangePicker, type DateRange } from '@/shared/ui/DateRangePicker'
import { cn, DUMMY_AVATAR, onAvatarError, localized, resolveName } from '@/shared/utils'
import {
  Search, Download, Calendar, MapPin, Phone, Mail,
  ChevronLeft, ChevronRight, X, Eye, BookOpen,
  Clock,
  Package, CreditCard, Infinity as InfinityIcon, Plus, RefreshCw,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(v?: string | null) {
  if (!v) return '—'
  try { return new Date(v).toLocaleDateString('en-GB').replace(/\//g, '-') } catch { return v }
}
function fmtDateTime(v?: string | null) {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleString('en-GB', {
      day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit'
    })
  } catch { return v }
}

// ── Status config ─────────────────────────────────────────────────────────────
function bookingStatusCfg(s: string, t: TFunction) {
  const u = s?.toUpperCase()
  if (u === 'PENDING')     return { cls: 'bg-[var(--Accents-Orange)] text-white',                    label: t('status.pending')     }
  if (u === 'CONFIRMED')   return { cls: 'bg-[var(--Accents-Blue)] text-white',                       label: t('status.confirmed')   }
  if (u === 'COMPLETED')   return { cls: 'bg-[var(--Accents-Green)] text-white',                      label: t('status.completed')   }
  if (u === 'CANCELLED')   return { cls: 'bg-[var(--Accents-Red)] text-white',                        label: t('status.cancelled')   }
  if (u === 'IN PROGRESS' || u === 'IN_PROGRESS')
                           return { cls: 'bg-[var(--Accents-Cyan)] text-white',                       label: t('status.inProgress') }
  return { cls: 'bg-gray-200 text-gray-600', label: s }
}
function cardBgByStatus(s: string) {
  const u = s?.toUpperCase()
  if (u === 'COMPLETED')   return 'bg-green-50/50'
  if (u === 'CANCELLED')   return 'bg-red-50/50'
  if (u === 'CONFIRMED')   return 'bg-brand-50/30'
  if (u === 'IN PROGRESS' || u === 'IN_PROGRESS') return 'bg-purple-50/30'
  return 'bg-brand-50/30'
}
const STATUS_KEY_MAP: Record<string, string> = {
  PENDING: 'pending', CONFIRMED: 'confirmed', COMPLETED: 'completed', CANCELLED: 'cancelled',
  IN_PROGRESS: 'inProgress', 'IN PROGRESS': 'inProgress', PAID: 'paid', FAILED: 'failed', REFUNDED: 'refunded',
}
function statusLabel(t: TFunction, raw?: string | null) {
  if (!raw) return raw
  const key = STATUS_KEY_MAP[raw.toUpperCase()]
  return key ? t(`status.${key}`, raw) : raw
}
function paymentStatusCls(s: string) {
  const u = s?.toUpperCase()
  if (u === 'PAID')        return 'bg-green-100 text-green-700'
  if (u === 'PENDING')     return 'bg-brand-100 text-brand-700'
  if (u === 'FAILED')      return 'bg-red-100 text-red-600'
  if (u === 'REFUNDED')    return 'bg-purple-100 text-purple-700'
  return 'bg-gray-100 text-gray-600'
}

// ── Booking card ──────────────────────────────────────────────────────────────
function BookingCard({ row, onView, isSchool, onChangeTrainer, onTrainerClick, onStudentClick }: {
  row: any; onView: () => void; isSchool?: boolean; onChangeTrainer?: () => void
  onTrainerClick?: (trainerId: number | string) => void
  onStudentClick?: (studentId: number | string) => void
}) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const user    = row.user    ?? {}
  const driver  = row.driver  ?? {}
  const pkg     = row.package ?? {}
  const status  = row.booking_status ?? row.status ?? 'PENDING'
  const cfg     = bookingStatusCfg(status, t)
  const userName   = localized(user, 'name', lang)
  const driverName = localized(driver, 'name', lang)

  const photoUrl = user.photo_url ?? null
  const bookingId = `#BK${String(row.id).padStart(4, '0')}`

  const totalSessions    = pkg.number_of_sessions ?? row.total_sessions ?? 0
  // Unlimited packages come back either as an explicit is_unlimited flag or as
  // number_of_sessions/-1 with no flag at all — checking both so "-1" never
  // shows up literally where the ∞ symbol should be.
  const isUnlimited      = pkg.is_unlimited === true || pkg.is_unlimited === 1 || Number(totalSessions) < 0
  const sessionCompleted = row.no_of_sessions_completed ?? 0

  const pkgName = resolveName(pkg, lang, 'package_translations', pkg?.name ?? '—')

  const paymentMethod = localized(row, 'payment_method', lang)
  const paymentStatus = row.payment_status ?? ''
  const total         = Number(row.total ?? row.sub_total ?? 0)

  // Next session info
  const upcomingSession = row.sessions?.find((s: any) =>
    s.session_status?.toUpperCase() === 'PENDING' || s.session_status?.toUpperCase() === 'CONFIRMED'
  )
  const lastSession = row.sessions?.find((s: any) =>
    s.session_status?.toUpperCase() === 'COMPLETED'
  )
  const cancelledSession = row.sessions?.find((s: any) =>
    s.session_status?.toUpperCase() === 'CANCELLED'
  )
  const inProgressSession = row.sessions?.find((s: any) =>
    s.session_status?.toUpperCase() === 'IN_PROGRESS' || s.session_status?.toUpperCase() === 'IN PROGRESS'
  )

  const highlightSession = inProgressSession ?? upcomingSession ?? lastSession ?? cancelledSession ?? row.sessions?.[0]
  const sessionLabel = inProgressSession ? t('status.inProgress') : lastSession ? t('bookingList.finalSessionCompleted')
    : cancelledSession ? t('bookingList.cancelledSession') : upcomingSession ? t('bookingList.upcomingSession') : null

  const sessionTime = highlightSession
    ? `${fmtDateTime(highlightSession.start_time)} - ${highlightSession.end_time ? new Date(highlightSession.end_time).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' }) : ''}`
    : null

  const trainerAccepted = row.trainer_accepted ?? true
  const city = resolveName(row.city, lang, 'city_translations', row.city_name ?? '—')

  // session label color
  const sessionLabelCls = inProgressSession ? 'bg-purple-100 text-purple-700'
    : lastSession ? 'bg-green-100 text-green-700'
    : cancelledSession ? 'bg-red-100 text-red-600'
    : 'bg-brand-50 text-brand-600'

  return (
    <div className={cn('rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden', cardBgByStatus(status))}>

      {/* ── Header ── */}
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={photoUrl || DUMMY_AVATAR} alt={userName} onError={onAvatarError}
            className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0 bg-gray-100"/>
          <div className="min-w-0">
            {user.id && onStudentClick
              ? <button type="button"
                  onClick={e=>{e.stopPropagation(); onStudentClick(user.id)}}
                  className="text-sm font-bold text-brand-600 hover:text-brand-700 hover:underline truncate text-start max-w-full">
                  {userName}
                </button>
              : <div className="text-sm font-bold text-gray-900 truncate">{userName}</div>}
            <div className="text-[12px] text-gray-400">{t('bookingList.bookingIdPrefix')}{bookingId}</div>
          </div>
        </div>
        <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold flex-shrink-0', cfg.cls)}>{cfg.label}</span>
      </div>

      {/* ── Contact row ── */}
      <div className="px-4 pb-2 space-y-0.5">
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1 min-w-0"><Mail size={10} className="text-gray-400 flex-shrink-0"/><span className="truncate">{user.email ?? '—'}</span></span>
          <span className="flex items-center gap-1 flex-shrink-0"><Phone size={10} className="text-gray-400"/>{user.phone ? `+${user.country_code??''} ${user.phone}` : '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Calendar size={10} className="text-gray-400"/>{fmtDateTime(row.created_at)}</span>
          <span className="flex items-center gap-1"><MapPin size={10} className="text-gray-400"/>{city}</span>
        </div>
      </div>

      {/* ── Trainer Info ── */}
      <div className="mx-4 mb-2 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80 border-b border-gray-100">{t('bookingList.trainerInfo')}</div>
        <div className="px-3 py-2">
          {driver.id && onTrainerClick
            ? <button type="button"
                onClick={e=>{e.stopPropagation(); onTrainerClick(driver.id)}}
                className="text-sm font-bold text-brand-600 hover:text-brand-700 hover:underline text-start">
                {driverName}
              </button>
            : <div className="text-sm font-bold text-gray-800">{driverName}</div>}
          <div className="text-[12px] text-gray-400">{t('bookingList.trainerIdPrefix')}{driver.id?.toString().padStart(3,'0') ?? '000'}</div>
        </div>
      </div>

      {/* ── Booking Info ── */}
      <div className="mx-4 mb-2 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80 border-b border-gray-100">{t('bookingList.bookingInfo')}</div>
        <div className="p-3">
          {/* Total sessions big number */}
          <div className="flex items-center justify-center mb-2">
            <div className="text-center">
              {isUnlimited
                ? <InfinityIcon size={28} className="text-gray-700 mx-auto"/>
                : <div className="text-2xl font-black text-gray-800">{totalSessions}</div>
              }
              <div className="text-[11px] text-gray-400 font-semibold uppercase">{t('bookingList.totalSession')}</div>
            </div>
          </div>
          {/* Session label */}
          {sessionLabel && sessionTime && (
            <div className={cn('mt-2 px-3 py-1.5 rounded-xl flex items-center justify-between text-xs font-semibold', sessionLabelCls)}>
              <span className="flex items-center gap-1">
                <Calendar size={10}/>{sessionLabel}
              </span>
              <span className="font-normal text-[12px]">{sessionTime}</span>
            </div>
          )}
          {/* Trainer not accepted warning */}
          {!trainerAccepted && (
            <div className="mt-2 px-3 py-1.5 rounded-xl bg-brand-50 border border-brand-200 text-xs text-brand-700 flex items-center gap-1">
              <Clock size={10}/>{t('bookingList.trainerNotAccepted')}
            </div>
          )}
          {/* In-progress progress bar */}
          {inProgressSession && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full bg-purple-500 rounded-full" style={{ width: `${inProgressSession.progress_percentage ?? 50}%` }}/>
              </div>
              <span className="text-[12px] text-purple-600 font-semibold">{inProgressSession.progress_percentage ?? 50}%</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Payment Info ── */}
      <div className="mx-4 mb-2 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80 border-b border-gray-100">{t('bookingList.paymentInfo')}</div>
        <div className="px-3 py-2 space-y-1.5">
          {/* Package */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="w-6 h-6 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                <Package size={11} className="text-brand-600"/>
              </div>
              <div>
                <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('bookingList.package')}</div>
                <div className="text-xs font-bold text-gray-800 truncate max-w-32">{pkgName}</div>
              </div>
            </div>
            <div className="text-sm font-black text-brand-600">AED {total.toLocaleString()}</div>
          </div>
          {/* Payment method + status */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5">
              <CreditCard size={11} className="text-gray-400"/>
              <div>
                <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('bookingList.paymentMethod')}</div>
                <div className="font-semibold text-gray-700">{paymentMethod}</div>
              </div>
            </div>
            {paymentStatus && (
              <span className={cn('px-2 py-0.5 rounded-lg text-[12px] font-bold', paymentStatusCls(paymentStatus))}>
                {statusLabel(t, paymentStatus)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── View Details / Change Trainer ── */}
      <div className="px-4 pb-4 flex items-center gap-2">
        <button
          onClick={onView}
          className="flex-1 py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors"
        >
          <Eye size={12}/> {t('bookingList.viewDetails')}
        </button>
        {isSchool && (
          <button
            onClick={onChangeTrainer}
            className="flex-1 py-2 rounded-xl border border-teal-300 text-teal-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-teal-50 transition-colors"
          >
            <RefreshCw size={12}/> {t('bookingList.changeTrainer')}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white animate-pulse overflow-hidden">
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0"/>
        <div className="flex-1 space-y-1.5"><div className="h-3 bg-gray-200 rounded w-2/3"/><div className="h-2 bg-gray-100 rounded w-1/2"/></div>
        <div className="h-5 w-16 rounded-full bg-gray-200"/>
      </div>
      <div className="px-4 pb-2 space-y-1"><div className="h-2 bg-gray-100 rounded w-3/4"/><div className="h-2 bg-gray-100 rounded w-1/2"/></div>
      {[1,2,3].map(i=><div key={i} className="mx-4 mb-2 h-14 bg-gray-100 rounded-xl"/>)}
      <div className="px-4 pb-4"><div className="h-8 bg-gray-100 rounded-xl"/></div>
    </div>
  )
}

// ── Paginator ─────────────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage, onPerPage }: {
  page: number; lastPage: number; total: number; perPage: number
  onPage: (p: number) => void; onPerPage: (n: number) => void
}) {
  const { t } = useTranslation()
  const nums = (() => {
    let s = Math.max(1, page-2); if(s+4>lastPage) s=Math.max(1,lastPage-4)
    return Array.from({length:Math.min(5,lastPage)},(_,i)=>s+i).filter(p=>p<=lastPage)
  })()
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap shadow-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{t('bookingList.itemsPerPage')}</span>
        <select value={perPage} onChange={e=>onPerPage(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400">
          {[10,15,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
        </select>
        <span>{t('bookingList.showingItems', { start: (page-1)*perPage+1, end: Math.min(page*perPage,total), total })}</span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={()=>onPage(Math.max(1,page-1))} disabled={page<=1}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft size={14} className="rtl:rotate-180"/>
        </button>
        {nums.map(p=>(
          <button key={p} onClick={()=>onPage(p)}
            className={cn('w-8 h-8 rounded-lg border text-xs font-semibold',
              p===page?'bg-brand-500 text-white border-brand-500':'border-gray-200 text-gray-600 hover:bg-gray-50')}>
            {p}
          </button>
        ))}
        <button onClick={()=>onPage(Math.min(lastPage,page+1))} disabled={page>=lastPage}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronRight size={14} className="rtl:rotate-180"/>
        </button>
        <span className="ms-1 text-xs text-gray-400">{t('bookingList.ofPages', { n: lastPage })}</span>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
const STATUS_TABS = [
  { value: 'all',         labelKey: 'bookingList.all'    },
  { value: 'PENDING',     labelKey: 'status.pending'     },
  { value: 'CONFIRMED',   labelKey: 'status.confirmed'   },
  { value: 'CANCELLED',   labelKey: 'status.cancelled'   },
  { value: 'COMPLETED',   labelKey: 'status.completed'   },
  { value: 'IN PROGRESS', labelKey: 'status.inProgress'  },
]

export function BookingListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isSchool = location.pathname.startsWith('/school')
  const bookingBase = isSchool ? '/school/booking' : '/dashboard/booking'
  const [activeTab, setActiveTab]   = useState('all')
  const [search,    setSearch]      = useState('')
  const [page,      setPage]        = useSessionState('booking-list:page', 1)
  const [perPage,   setPerPage]     = useSessionState('booking-list:perPage', 20)
  // Empty by default — the list shows every booking until a range is picked.
  const [dateRange, setDateRange]   = useSessionState<DateRange>('booking-list:dateRange', { from: '', to: '' })
  const [changeTrainerRow, setChangeTrainerRow] = useState<any | null>(null)

  const debouncedSearch = useDebounce(search)

  // Live booking-status enum — the tab's display value (e.g. "COMPLETED") doesn't
  // always match what the list endpoint expects for `booking_status`; the old
  // dashboard resolves this by sending the enum's own `value`/`id`, matched by
  // name, rather than the normalized display string. Same `.values`-first shape
  // as BookingDetailsPage's status dropdown.
  const { data: statusEnumRaw } = useQuery({
    queryKey: ['booking-status-enum'],
    queryFn: () => restClient.get(endpoints.booking.getBookingStatus).then(r => r.data),
    staleTime: Infinity,
    enabled: !isSchool,
  })
  const statusEnumOptions: any[] = Array.isArray((statusEnumRaw as any)?.values) ? (statusEnumRaw as any).values
    : Array.isArray((statusEnumRaw as any)?.data) ? (statusEnumRaw as any).data
    : Array.isArray(statusEnumRaw) ? statusEnumRaw : []
  const apiStatusFor = (tabValue: string) => {
    const norm = tabValue.replace(/\s+/g, '_').toUpperCase()
    const match = statusEnumOptions.find((o: any) => String(o.name ?? '').replace(/\s+/g, '_').toUpperCase() === norm)
    return match ? String(match.value ?? match.id ?? tabValue) : tabValue
  }

  // A purely-numeric search term is treated as a booking ID lookup (the list
  // endpoint's `search` param only matches student/trainer/school name server-side —
  // it has no booking-ID filter — so an ID search goes through get-booking-by-id instead).
  const isIdSearch = /^\d+$/.test(debouncedSearch.trim())

  const { data, isLoading } = useQuery({
    queryKey: ['bookings', 'list', isSchool, { activeTab, page, perPage, search: debouncedSearch, isIdSearch, dateRange }, statusEnumOptions.length],
    queryFn: () => {
      if (isIdSearch) {
        // 404 (no booking with that id) → null, so the page just renders the
        // existing "no bookings found" empty state instead of an error screen.
        return bookingService.getById(Number(debouncedSearch.trim()), isSchool).catch(() => null)
      }
      const params = {
        page, limit: perPage,
        search: debouncedSearch || undefined,
        booking_status: activeTab !== 'all' ? apiStatusFor(activeTab) : undefined,
        start_date: dateRange.from || undefined,
        end_date: dateRange.to || undefined,
      }
      return isSchool ? bookingService.listSchoolAdmin(params) : bookingService.list(params)
    },
    staleTime: 30_000,
  })

  useScrollRestoration('booking-list', !isLoading)

  // Visiting this page is what "checks" the sidebar's Bookings badge — sync the
  // current pending total as "seen" so the badge clears (school admins use a
  // different bookings endpoint/permission set, so this stays disabled there).
  const { data: pendingTotalForSeen } = usePendingBookingsCount(!isSchool)
  const markPendingSeen = useBookingsSeenStore(s => s.markPendingSeen)
  useEffect(() => {
    if (!isSchool && pendingTotalForSeen != null) markPendingSeen(pendingTotalForSeen)
  }, [isSchool, pendingTotalForSeen, markPendingSeen])

  const rows: any[] = isIdSearch
    ? ((data as any)?.data ? [(data as any).data] : [])
    : ((data as any)?.data?.bookings ?? (data as any)?.bookings ?? (data as any)?.data ?? [])
  // The list endpoints return pagination info as a top-level `total` field
  // (not nested under `data`/`meta`) — reading the wrong key silently fell back
  // to `rows.length`, which is never more than one page's worth, so pagination
  // looked "stuck" on a single page no matter how many bookings existed.
  const total    = isIdSearch ? rows.length : ((data as any)?.total ?? (data as any)?.data?.total_count ?? (data as any)?.total_count ?? (data as any)?.meta?.total ?? rows.length)
  const lastPage = Math.ceil(total / perPage) || 1

  // Booking status counts from API
  const statusCounts = isIdSearch ? {} : ((data as any)?.data?.booking_status_counts ?? (data as any)?.booking_status_counts ?? {})
  const totalAll = Object.values(statusCounts).reduce((a: number, b: any) => a + Number(b), 0) as number

  const handleExport = () => {
    const csv = [[t('bookingList.csvId'),t('bookingList.csvStudent'),t('bookingList.csvTrainer'),t('bookingList.csvStatus'),t('bookingList.csvPayment'),t('bookingList.csvTotal'),t('bookingList.csvCreated')],
      ...rows.map((r:any)=>[r.id,r.user?.name??'',r.driver?.name??'',r.booking_status??'',r.payment_status??'',r.total??'',r.created_at??''])
    ].map(r=>r.join(',')).join('\n')
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download='bookings.csv'; a.click()
  }

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span>{t('bookingList.breadcrumbDashboard')}</span><span>/</span>
            <span className="text-brand-500 font-semibold">{t('bookingList.breadcrumbBookings')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{t('bookingList.title')}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
            <Download size={12}/> {t('bookingList.download')}
          </button>
          <DateRangePicker
            from={dateRange.from} to={dateRange.to}
            onChange={r => { setDateRange(r); setPage(1) }}/>
          <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 bg-white w-full sm:w-80">
            <Search size={16} className="text-gray-400 flex-shrink-0"/>
            <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder={t('bookingList.searchByName')}
              className="text-sm text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0"/>
            {search && <button onClick={()=>setSearch('')}><X size={14} className="text-gray-300 hover:text-gray-500"/></button>}
          </div>
          <button type="button"
            onClick={() => window.open('http://assistant.drivys.com/', '_blank', 'noopener,noreferrer')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-colors">
            <Plus size={13}/> {t('bookingList.createNewBooking')}
          </button>
        </div>
      </div>

      {/* ── Tab bar (hidden for school admins) ── */}
      {!isSchool && (
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map(tb => {
          const count = tb.value === 'all' ? (totalAll || total)
            : statusCounts[tb.value] ?? statusCounts[tb.value.replace(' ','_')] ?? 0
          const isActive = activeTab === tb.value
          const activeCls = {
            'all':         'bg-gray-700 text-white border-gray-700',
            'PENDING':     'bg-[var(--Accents-Orange)] text-white border-transparent',
            'CONFIRMED':   'bg-[var(--Accents-Blue)] text-white border-transparent',
            'CANCELLED':   'bg-[var(--Accents-Red)] text-white border-transparent',
            'COMPLETED':   'bg-[var(--Accents-Green)] text-white border-transparent',
            'IN PROGRESS': 'bg-[var(--Accents-Cyan)] text-white border-transparent',
          }[tb.value] ?? 'bg-[var(--Accents-Orange)] text-white border-transparent'
          return (
            <button key={tb.value} onClick={()=>{setActiveTab(tb.value);setPage(1)}}
              className={cn('px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors flex items-center gap-1.5',
                isActive ? activeCls : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600')}>
              {t(tb.labelKey)}
              <span className={cn('px-1.5 py-0.5 rounded text-[12px] font-bold',
                isActive ? 'bg-white/20' : 'bg-gray-100 text-gray-500')}>
                {count}
              </span>
            </button>
          )
        })}
      </div>
      )}

      {/* ── Card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({length:6}).map((_,i)=><SkeletonCard key={i}/>)
          : rows.length === 0
            ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <BookOpen size={24} className="text-gray-300"/>
                </div>
                <p className="text-sm font-semibold text-gray-500">{t('bookingList.noBookingsFound')}</p>
              </div>
            )
            : rows.map((row:any)=>(
              <BookingCard key={row.id} row={row}
                onView={()=>navigate(`${bookingBase}/${row.id}`)}
                isSchool={isSchool}
                onChangeTrainer={()=>setChangeTrainerRow(row)}
                onTrainerClick={tid=>navigate(isSchool ? `/school/trainer/${tid}` : `/dashboard/user/${tid}`)}
                // School admins have no student-detail route, so the name stays plain text there.
                onStudentClick={isSchool ? undefined : sid=>navigate(`/dashboard/user/${sid}`)}/>
            ))
        }
      </div>

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <Paginator page={page} lastPage={lastPage} total={total} perPage={perPage}
          onPage={setPage} onPerPage={n=>{setPerPage(n);setPage(1)}}/>
      )}

      {/* ── Change Trainer Modal ── */}
      {changeTrainerRow && (
        <ChangeTrainerModal
          key={changeTrainerRow.id}
          open={!!changeTrainerRow}
          onClose={()=>setChangeTrainerRow(null)}
          booking={changeTrainerRow}
        />
      )}
    </div>
  )
}
